import EmphasizableCaption from './EmpasizableCaption';
import SettingsSection from './SettingsSection';
import {
  Box,
  Chip,
  FormGroup,
  Grid,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { MaterialNames } from 'Constants';
import { useCallback, useState } from 'react';
import { useRecoilState } from 'recoil';
import { BoardType, Material, Materials, OneArray, TwoArray } from 'Types';

import { getBoardTypes, getCtor } from 'boardClasses/BoardFactory';
import MaterialThumb from 'designer/MaterialThumb';
import NumberInput from 'designer/NumberInput';
import { settingsAtom } from 'state/recoilState';
import { cloneDeep, getDisplayLengthInUnits } from 'utils/utils';

function RandomBoardSettings() {
  const [settings, setSettings] = useRecoilState(settingsAtom);

  const [stylesError, setStylesError] = useState<BoardType | null>(null);
  const [materialsError, setMaterialsError] = useState<Material | null>(null);

  const theme = useTheme();

  const displayLength = getDisplayLengthInUnits(settings.units);

  const onRandomWidthChange = useCallback(
    (width: number) => {
      setSettings((prevSettings) => {
        const newSettings = cloneDeep(prevSettings);
        newSettings.random.size.width = width;
        return newSettings;
      });
    },
    [setSettings]
  );

  const onRandomHeightChange = useCallback(
    (height: number) => {
      setSettings((prevSettings) => {
        const newSettings = cloneDeep(prevSettings);
        newSettings.random.size.height = height;
        return newSettings;
      });
    },
    [setSettings]
  );

  const toggleStylesError = useCallback((type: BoardType) => {
    setStylesError(type);

    setTimeout(() => {
      setStylesError(null);
    }, 1000);
  }, []);

  const toggleMaterialsError = useCallback((material: Material) => {
    setMaterialsError(material);

    setTimeout(() => {
      setMaterialsError(null);
    }, 1000);
  }, []);

  const onRandomStyleChange = (boardType: BoardType) => {
    const newStyles = settings.random.styles.includes(boardType)
      ? settings.random.styles.filter((key) => key !== boardType)
      : settings.random.styles.concat(boardType);

    if (
      newStyles.length < 1 &&
      newStyles.length < settings.random.styles.length
    ) {
      toggleStylesError(boardType);
      return;
    }

    setSettings((prevSettings) => ({
      ...prevSettings,
      random: {
        ...settings.random,
        styles: newStyles as OneArray<BoardType>,
      },
    }));
  };

  const onRandomMaterialChange = (material: Material) => {
    const newMaterials = settings.random.materials.includes(material)
      ? settings.random.materials.filter((key) => key !== material)
      : settings.random.materials.concat(material);

    if (newMaterials.length < 2) {
      toggleMaterialsError(material);
      return;
    }

    setSettings((prevSettings) => ({
      ...prevSettings,
      random: {
        ...settings.random,
        materials: newMaterials as TwoArray<Material>,
      },
    }));
  };

  return (
    <SettingsSection title="Random Board Generator">
      <Box sx={{ pb: 2 }}>
        <Typography variant="h6" color="inherit">
          Board Dimensions
        </Typography>
      </Box>

      <Grid container spacing={2}>
        <Grid item>
          <NumberInput
            id="random-width"
            label="Width"
            step={1 / 8}
            value={settings.random.size.width}
            onChange={onRandomWidthChange}
            size={80}
            units={settings.units}
          />
        </Grid>
        <Grid item>
          <NumberInput
            id="random-height"
            label="Height"
            step={1 / 8}
            value={settings.random.size.height}
            onChange={onRandomHeightChange}
            size={80}
            units={settings.units}
          />
        </Grid>
      </Grid>

      <Typography variant="caption">
        Quarter Square boards will be{' '}
        {displayLength(settings.random.size.width)} x{' '}
        {displayLength(settings.random.size.width)}.
      </Typography>
      <br />
      <Typography variant="caption">
        Weave boards will be {displayLength(settings.random.size.width)} wide
        with variable length.
      </Typography>

      <Box sx={{ pt: 2 }}>
        <Typography variant="h6" color="inherit">
          Board Styles
        </Typography>
        <EmphasizableCaption hasError={stylesError != null}>
          Must choose at least one.
        </EmphasizableCaption>
      </Box>

      <Box>
        <FormGroup row>
          {getBoardTypes().map((boardType) => {
            const supportsRandom = 'createRandom' in getCtor(boardType);
            const isFilled = supportsRandom
              ? settings.random.styles.includes(boardType)
                ? true
                : false
              : false;

            return (
              <Tooltip
                disableFocusListener={supportsRandom}
                disableHoverListener={supportsRandom}
                disableTouchListener={supportsRandom}
                key={boardType}
                placement="top"
                style={{ maxWidth: 'none' }}
                title={`${boardType} does not support random generation at this time`}
              >
                <Chip
                  clickable
                  disabled={!supportsRandom}
                  color={stylesError === boardType ? 'secondary' : 'primary'}
                  label={boardType}
                  onClick={() => onRandomStyleChange(boardType)}
                  sx={{
                    marginRight: theme.spacing(1),
                    marginTop: theme.spacing(1),
                    ...(isFilled ? { paddingRight: '2px' } : null),
                  }}
                  variant={isFilled ? 'filled' : 'outlined'}
                />
              </Tooltip>
            );
          })}
        </FormGroup>
      </Box>

      <Box sx={{ pt: 2 }}>
        <Typography variant="h6" color="inherit">
          Board Materials
        </Typography>
        <EmphasizableCaption hasError={materialsError != null}>
          Must choose at least two.
        </EmphasizableCaption>
      </Box>

      <Box>
        <FormGroup row>
          {Materials.map((material) => {
            const isFilled = settings.random.materials.includes(material)
              ? true
              : false;

            return (
              <Chip
                avatar={
                  <Box
                    sx={{
                      display: 'flex',
                      borderRadius: '50%',
                      overflow: 'hidden',
                      border: `1px solid ${theme.palette.background.paper}`,
                    }}
                  >
                    <MaterialThumb material={material} size={24} />
                  </Box>
                }
                clickable
                color={materialsError === material ? 'secondary' : 'primary'}
                key={material}
                label={MaterialNames[material]}
                onClick={() => onRandomMaterialChange(material)}
                sx={{
                  marginRight: theme.spacing(1),
                  marginTop: theme.spacing(1),
                  ...(isFilled ? { paddingRight: '1px' } : null),
                }}
                variant={isFilled ? 'filled' : 'outlined'}
              />
            );
          })}
        </FormGroup>
      </Box>
    </SettingsSection>
  );
}

export default RandomBoardSettings;
